import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { Help as HelpTrigger } from '@atlaskit/navigation-system/top-nav-items';
import type { AnyEntryPoint } from '@atlassian/entry-point-types/types';
import { PanelTrigger, type PanelTriggerRenderProps } from '@atlassian/entry-points/panel-trigger';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import type { HelpProps, OnHelpButtonPressed } from '../../common/types';
import { messages } from './messages';

/**
 * `HelpProps` plus the panel wiring GlobalRoot supplies through the top nav
 * context, which is internal and so is not part of the public props.
 */
export type PlatformHelpProps<TEntryPoint extends AnyEntryPoint> = HelpProps<TEntryPoint> & {
	closeHelpPanel?: () => void;
	isHelpPanelOpen?: boolean;
	openHelpPanel?: OnHelpButtonPressed;
};

/**
 * Platform default for `HelpSlot`.
 *
 * The Help trigger is eager and preserves ADS's optional unread-content badge.
 * App content is supplied as an entry point and is preloaded on interaction;
 * GlobalRoot renders the loaded content in a responsive page-layout end panel.
 */
export function Help<TEntryPoint extends AnyEntryPoint>({
	badge,
	closeHelpPanel,
	entryPoint,
	entryPointParams,
	entryPointProps,
	errorFallback,
	fallback,
	isHelpPanelOpen,
	isSelected,
	label,
	onClick,
	openHelpPanel: openPanel,
	testId,
	triggerRef,
}: PlatformHelpProps<TEntryPoint>): JSX.Element | null {
	const { formatMessage } = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const fireHelpClicked = useCallback(
		(slotType: 'button' | 'panel') => {
			fireEvent('ui.topNav.clicked.help', { slotType });
		},
		[fireEvent],
	);
	const resolveLabel = useCallback(
		() => label ?? formatMessage(messages.help),
		[formatMessage, label],
	);
	const openHelpPanel = useCallback(
		(content: React.ReactNode) => openPanel?.(content),
		[openPanel],
	);

	const renderTrigger = useCallback(
		({ isSelected: panelIsOpen, onClick: onTriggerClicked, ref }: PanelTriggerRenderProps) => {
			return (
				<HelpTrigger
					badge={badge}
					isListItem
					isSelected={isSelected ?? panelIsOpen}
					label={resolveLabel()}
					onClick={() => {
						fireHelpClicked('panel');
						onTriggerClicked();
					}}
					ref={ref}
					testId={testId}
				/>
			);
		},
		[badge, fireHelpClicked, isSelected, resolveLabel, testId],
	);

	if (!entryPoint) {
		// HelpProps guarantees an onClick when there's no entryPoint, but TS can't apply that here: TEntryPoint is still an
		// unresolved type parameter inside this generic function, so it can't rule out the entryPoint variant and onClick
		// keeps its undefined constituent. Callers are still held to the union.
		if (!onClick) {
			return null;
		}

		return (
			<HelpTrigger
				badge={badge}
				isListItem
				isSelected={isSelected}
				label={resolveLabel()}
				onClick={(event) => {
					fireHelpClicked('button');
					onClick(event);
				}}
				ref={triggerRef}
				testId={testId}
			/>
		);
	}

	if (!openPanel || !closeHelpPanel) {
		return null;
	}

	return (
		<PanelTrigger
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			errorFallback={errorFallback}
			fallback={fallback}
			isPanelOpen={isHelpPanelOpen ?? false}
			onClosePanel={closeHelpPanel}
			onOpenPanel={openHelpPanel}
			trigger={renderTrigger}
			triggerRef={triggerRef}
		/>
	);
}
